import type { IObservableArray } from 'mobx';
import { runInAction } from 'mobx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { BannersnackCreative, DisplayCreative } from '@feathr/blackbox';
import { CreativeClass } from '@feathr/blackbox';
import { showError } from '@feathr/components';
import { useAccount, useStore } from '@feathr/extender/state';

import { syncAndValidateBannersnackCreative } from '../Creatives.utils';

export type TBannersnackBuildStep = 'editor' | 'creativeModal';

export interface IUseAddBannersnackBuildFlowParams {
  /** Campaign ID to associate the creative with. If not provided, creative is created at the event level. */
  campaignId?: string;
  /** Event ID to associate the creative with when no campaign is provided. */
  eventId?: string;
  creatives?: IObservableArray<DisplayCreative>;
  onAppear?: (id: string) => void;
  onComplete: () => void;
  onBack: () => void;
  onCancel: () => void;
}

export interface IUseAddBannersnackBuildFlowReturn {
  step: TBannersnackBuildStep;
  creative: BannersnackCreative | undefined;
  /** True while creating the creative or syncing after editor — disables action buttons */
  isLoading: boolean;
  handleEditorNext: () => Promise<void>;
  handleCloseModal: () => Promise<void>;
  handleBackToEditor: () => void;
  handleBack: () => void;
  handleCancel: () => void;
}

/**
 * Manages state and logic for the AddBannersnackBuildFlow component.
 *
 * @param params - Hook parameters
 * @returns State and handlers for the flow
 */
export function useAddBannersnackBuildFlow(
  params: IUseAddBannersnackBuildFlowParams,
): IUseAddBannersnackBuildFlowReturn {
  const { campaignId, eventId, creatives, onAppear, onComplete, onBack, onCancel } = params;
  const { t } = useTranslation();
  const account = useAccount();
  const { Creatives } = useStore();

  const [step, setStep] = useState<TBannersnackBuildStep>('editor');
  const [creative, setCreative] = useState<BannersnackCreative | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const isCreatingRef = useRef(false);
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  useEffect(() => {
    if (isCreatingRef.current) {
      return;
    }
    isCreatingRef.current = true;

    async function createCreative(): Promise<void> {
      try {
        // Casts are safe here — we're creating with _cls: DisplayBannersnack
        // but the generic Creatives collection API returns base types
        const newCreative = Creatives.create({
          mimetype: 'text/html',
          _cls: CreativeClass.DisplayBannersnack,
          spec: 'medium_rectangle',
          width: 300,
          height: 250,
          ...(campaignId ? { parent: campaignId } : {}),
          ...(eventId && !campaignId ? { event: eventId } : {}),
          is_enabled: true,
          account: account.id,
          name: t('Unnamed Banner Builder'),
        }) as BannersnackCreative;

        const savedCreative = (await Creatives.add(newCreative, {
          refreshApiCache: false,
        })) as BannersnackCreative;

        setCreative(savedCreative);
        setIsLoading(false);
      } catch {
        showError(t('Failed to create creative. Please try again.'), t);
        onCancelRef.current();
      }
    }

    createCreative();
  }, [Creatives, account.id, campaignId, eventId, t]);

  const handleEditorNext = useCallback(async (): Promise<void> => {
    if (!creative) {
      return;
    }

    setIsLoading(true);
    try {
      const isValid = await syncAndValidateBannersnackCreative(creative, t);
      if (!isValid) {
        return;
      }
    } finally {
      setIsLoading(false);
    }

    setStep('creativeModal');
  }, [creative, t]);

  const handleCloseModal = useCallback(async (): Promise<void> => {
    if (!creative) {
      return;
    }

    try {
      if (creative.isDirty) {
        await creative.patchDirty();
      }
    } catch {
      showError(t('Failed to save creative changes.'), t);
    }

    runInAction(() => creatives?.unshift(creative));
    onAppear?.(creative.id);
    onComplete();
  }, [creative, creatives, onAppear, onComplete, t]);

  const handleBackToEditor = useCallback((): void => {
    setStep('editor');
  }, []);

  const handleCancel = useCallback((): void => {
    if (creative) {
      try {
        runInAction(() => creative.set({ is_archived: true }));
        creative.patchDirty().catch(() => {});
      } catch {
        // Fire-and-forget — don't block cancel if archive fails
      }
    }
    onCancel();
  }, [creative, onCancel]);

  return {
    step,
    creative,
    isLoading,
    handleEditorNext,
    handleCloseModal,
    handleBackToEditor,
    handleBack: onBack,
    handleCancel,
  };
}
